import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import { formatMillimes, tndStringToMillimes } from '../../lib/money';
import type { InvokeResponseBase } from '../../lib/types';

interface SceEntryForm {
  id: string;
  sequence_number: number;
  entry_date: string;
  fiscal_year: number;
  debit_account: string;
  credit_account: string;
  amount_tnd: string;
  label: string;
}

interface DgiFinding {
  axis: 'journal_sce' | 'is' | 'deductibilite' | 'cca' | 'recettes' | 'tva' | 'immobilisations' | 'ras';
  severity: 'high' | 'medium' | 'low';
  description: string;
  amount_at_risk_millimes: number;
  recommended_action: string;
  affected_entry_ids: string[];
  legal_basis_ref: string;
}

interface DgiResult {
  fiscal_year: number;
  company_name: string;
  findings: DgiFinding[];
  findings_count_by_severity: { high: number; medium: number; low: number };
  findings_count_by_axis: Record<string, number>;
  total_amount_at_risk_millimes: number;
  risk_level: 'none' | 'low' | 'medium' | 'high' | 'critical';
}

const SEVERITY_STYLE: Record<DgiFinding['severity'], string> = {
  high: 'bg-red-100 text-red-900 border-red-300',
  medium: 'bg-amber-100 text-amber-900 border-amber-300',
  low: 'bg-blue-100 text-blue-900 border-blue-300',
};

const RISK_STYLE: Record<DgiResult['risk_level'], string> = {
  none: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  low: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  medium: 'bg-amber-100 text-amber-900 border-amber-300',
  high: 'bg-orange-100 text-orange-900 border-orange-300',
  critical: 'bg-red-100 text-red-900 border-red-300',
};

const AXIS_LABEL: Record<DgiFinding['axis'], string> = {
  journal_sce: 'Journal SCE',
  is: 'IS',
  deductibilite: 'Déductibilité',
  cca: 'CCA',
  recettes: 'Recettes',
  tva: 'TVA',
  immobilisations: 'Immobilisations',
  ras: 'RAS',
};

const SAMPLE_ENTRIES: SceEntryForm[] = [
  { id: 'e1', sequence_number: 1, entry_date: '2026-04-15', fiscal_year: 2026, debit_account: '618', credit_account: '532', amount_tnd: '500.000', label: 'Amende pour retard de paiement' },
  { id: 'e2', sequence_number: 5, entry_date: '2026-05-10', fiscal_year: 2026, debit_account: '622', credit_account: '401', amount_tnd: '2000.000', label: 'Honoraires avocat' },
  { id: 'e3', sequence_number: 6, entry_date: '2026-05-10', fiscal_year: 2026, debit_account: '618', credit_account: '532', amount_tnd: '300.000', label: 'Cadeaux clients' },
];

export function ControleurDgiPage() {
  const [fiscalYear, setFiscalYear] = useState(2026);
  const [companyName, setCompanyName] = useState('Sahel Textile Industries');
  const [sector, setSector] = useState('');
  const [revenueTotal, setRevenueTotal] = useState('100000.000');
  const [entries, setEntries] = useState<SceEntryForm[]>(SAMPLE_ENTRIES);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  function update(i: number, patch: Partial<SceEntryForm>) {
    setEntries((p) => p.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  }
  function add() {
    setEntries((p) => [...p, { id: `e${p.length + 1}`, sequence_number: p.length + 1, entry_date: '2026-01-01', fiscal_year: fiscalYear, debit_account: '614', credit_account: '532', amount_tnd: '0.000', label: '' }]);
  }
  function remove(i: number) {
    setEntries((p) => p.filter((_, idx) => idx !== i));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const payload: Record<string, unknown> = {
        fiscal_year: fiscalYear,
        company_name: companyName,
        revenue_accounts_total_millimes: tndStringToMillimes(revenueTotal),
        sce_entries: entries.map((e) => ({
          id: e.id,
          sequence_number: e.sequence_number,
          entry_date: e.entry_date,
          fiscal_year: e.fiscal_year,
          debit_account: e.debit_account,
          credit_account: e.credit_account,
          amount_millimes: tndStringToMillimes(e.amount_tnd),
          label: e.label,
        })),
        ttn_invoices: [],
      };
      if (sector) payload.sector_code = sector;
      const res = await api.invokeAgent('controleur_dgi', { payload, fiscal_reference_date: `${fiscalYear}-12-31` });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AgentPageShell
      title="Contrôleur DGI ★"
      module="Fiscal · Phase 3"
      model="Opus 4.7"
      description={
        <>
          Audit fiscal complet sur 7 axes — journal SCE, IS, déductibilité, CCA, recettes (TTN ↔ 70x),
          TVA, immobilisations, RAS. Identifie les anomalies et chiffre le risque cumulé en cas
          de redressement.
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="Société auditée">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Field label="Raison sociale"><input className={inputCls} value={companyName} onChange={(e) => setCompanyName(e.target.value)} required /></Field>
            <Field label="Année fiscale"><input type="number" className={inputCls} value={fiscalYear} onChange={(e) => setFiscalYear(parseInt(e.target.value || '2026', 10))} /></Field>
            <Field label="Secteur">
              <select className={inputCls} value={sector} onChange={(e) => setSector(e.target.value)}>
                <option value="">Standard</option>
                <option value="industrie_export">Industrie export</option>
                <option value="banque_assurance">Banque/Assurance</option>
                <option value="hydrocarbures">Hydrocarbures</option>
              </select>
            </Field>
            <Field label="Total comptes 70x (TND)"><input className={inputCls + ' font-mono'} value={revenueTotal} onChange={(e) => setRevenueTotal(e.target.value)} required /></Field>
          </div>
        </FormCard>

        <FormCard
          title={`Écritures à auditer (${entries.length})`}
          action={<button type="button" onClick={add} className="text-xs px-2 py-1 rounded bg-ink text-cream hover:opacity-90">+ Écriture</button>}
        >
          <div className="overflow-x-auto -mx-2 max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-cream/40">
                <tr className="text-xs uppercase tracking-wider text-taupe border-b border-border">
                  <th className="px-1 py-2 text-left">ID</th>
                  <th className="px-1 py-2 text-right">Seq.</th>
                  <th className="px-1 py-2 text-left">Date</th>
                  <th className="px-1 py-2 text-left">Compte D</th>
                  <th className="px-1 py-2 text-left">Compte C</th>
                  <th className="px-1 py-2 text-right">Montant (TND)</th>
                  <th className="px-1 py-2 text-left">Libellé</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {entries.map((e, idx) => (
                  <tr key={idx} className="border-b border-border/40">
                    <td className="px-1 py-1"><input className={inputCls + ' font-mono py-1'} value={e.id} onChange={(ev) => update(idx, { id: ev.target.value })} required /></td>
                    <td className="px-1 py-1"><input type="number" className={inputCls + ' font-mono py-1 text-right'} value={e.sequence_number} onChange={(ev) => update(idx, { sequence_number: parseInt(ev.target.value || '0', 10) })} /></td>
                    <td className="px-1 py-1"><input type="date" className={inputCls + ' py-1'} value={e.entry_date} onChange={(ev) => update(idx, { entry_date: ev.target.value })} /></td>
                    <td className="px-1 py-1"><input className={inputCls + ' font-mono py-1'} value={e.debit_account} onChange={(ev) => update(idx, { debit_account: ev.target.value })} required /></td>
                    <td className="px-1 py-1"><input className={inputCls + ' font-mono py-1'} value={e.credit_account} onChange={(ev) => update(idx, { credit_account: ev.target.value })} required /></td>
                    <td className="px-1 py-1"><input className={inputCls + ' font-mono py-1 text-right'} value={e.amount_tnd} onChange={(ev) => update(idx, { amount_tnd: ev.target.value })} /></td>
                    <td className="px-1 py-1"><input className={inputCls + ' py-1'} value={e.label} onChange={(ev) => update(idx, { label: ev.target.value })} required /></td>
                    <td className="px-1">{entries.length > 1 && <button type="button" onClick={() => remove(idx)} className="text-xs text-red-700">×</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </FormCard>

        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Audit en cours…' : 'Lancer l\'audit DGI'}
        </button>
      </form>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <DgiResultView result={result as DgiResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function DgiResultView({ result }: { result: DgiResult }) {
  return (
    <div className="space-y-5">
      <section className={`p-4 rounded border-2 ${RISK_STYLE[result.risk_level]}`}>
        <div className="text-xs uppercase tracking-wider mb-1">Niveau de risque</div>
        <div className="font-serif text-2xl font-bold">{result.risk_level.toUpperCase()}</div>
        <div className="text-sm mt-2">
          {result.findings.length} finding(s) · risque cumulé estimé {formatMillimes(result.total_amount_at_risk_millimes)}
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Répartition par sévérité</div>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <Stat label="High" value={String(result.findings_count_by_severity.high)} cls="text-red-700 border-red-300 bg-red-50" />
          <Stat label="Medium" value={String(result.findings_count_by_severity.medium)} cls="text-amber-700 border-amber-300 bg-amber-50" />
          <Stat label="Low" value={String(result.findings_count_by_severity.low)} cls="text-blue-700 border-blue-300 bg-blue-50" />
        </div>
      </section>

      {Object.keys(result.findings_count_by_axis).length > 0 && (
        <section>
          <div className="text-xs uppercase tracking-wider text-taupe mb-2">Répartition par axe</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(result.findings_count_by_axis).map(([axis, count]) => (
              <span key={axis} className="text-xs px-2 py-1 rounded bg-white border border-border">
                <strong>{AXIS_LABEL[axis as DgiFinding['axis']] ?? axis}</strong> · {count}
              </span>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Findings ({result.findings.length})</div>
        {result.findings.length === 0 ? (
          <div className="card text-sm text-emerald-800 bg-emerald-50/40 border border-emerald-200">
            ✓ Aucune anomalie détectée. Les 7 axes contrôlés sont conformes.
          </div>
        ) : (
          <div className="space-y-2">
            {result.findings.map((f, i) => (
              <div key={i} className={`p-3 rounded border ${SEVERITY_STYLE[f.severity]}`}>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded bg-white border border-zinc-300">
                    {f.severity}
                  </span>
                  <strong>{AXIS_LABEL[f.axis]}</strong>
                  {f.amount_at_risk_millimes > 0 && (
                    <span className="text-xs font-mono ml-auto">
                      Risque ~ <strong>{formatMillimes(f.amount_at_risk_millimes)}</strong>
                    </span>
                  )}
                </div>
                <p className="text-sm">{f.description}</p>
                <div className="mt-2 text-xs">
                  <strong>Action recommandée :</strong> {f.recommended_action}
                </div>
                <div className="text-xs text-taupe italic mt-1">{f.legal_basis_ref}</div>
                {f.affected_entry_ids.length > 0 && (
                  <div className="text-xs mt-1 font-mono">
                    Écritures concernées : {f.affected_entry_ids.join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, cls }: { label: string; value: string; cls: string }) {
  return (
    <div className={`p-3 rounded border text-center ${cls}`}>
      <div className="text-xs uppercase tracking-wider opacity-80">{label}</div>
      <div className="font-mono text-2xl font-bold">{value}</div>
    </div>
  );
}
